import { useEffect, useRef } from "react";
import { __ } from "../../../utils/i18n.js";
/**
 * Inactive-tab attention loop. Once the tab goes hidden:
 *   +10 s → flip to alternative title
 *   +15 s → alternate every 5 s
 *   +30 s → prefix "(1)" (reads like an unread notification)
 *   +60 s → stop, leave the "(1)" variant as the resting state
 * Tab visible or view=success restores the original title and tears down timers.
 */ function useHtmlTitle(store, productName) {
    const originalTitleRef = useRef(document.title);
    const timersRef = useRef([]);
    useEffect(()=>{
        if (!productName || store.view !== "checkout") {
            document.title = originalTitleRef.current;
            timersRef.current.forEach(clearTimeout);
            timersRef.current = [];
            return undefined;
        }
        const titleA = __("%s - Complete purchase", productName);
        const titleB = __("Complete checkout - %s", productName);
        document.title = titleA;
        let elapsed = 0;
        let isAlternative = false;
        const onVisibilityChange = ()=>{
            if (document.hidden) {
                const firstChange = setTimeout(()=>{
                    isAlternative = true;
                    document.title = titleB;
                    elapsed = 10;
                    const interval = setInterval(()=>{
                        elapsed += 5;
                        if (elapsed >= 60) {
                            clearInterval(interval);
                            document.title = elapsed >= 30 ? `(1) ${titleB}` : titleB;
                            return;
                        }
                        isAlternative = !isAlternative;
                        const prefix = elapsed >= 30 ? "(1) " : "";
                        document.title = prefix + (isAlternative ? titleB : titleA);
                    }, 5000);
                    timersRef.current.push(interval);
                }, 10000);
                timersRef.current.push(firstChange);
            } else {
                timersRef.current.forEach(clearTimeout);
                timersRef.current = [];
                document.title = titleA;
                elapsed = 0;
                isAlternative = false;
            }
        };
        document.addEventListener("visibilitychange", onVisibilityChange);
        return ()=>{
            document.removeEventListener("visibilitychange", onVisibilityChange);
            timersRef.current.forEach(clearTimeout);
            timersRef.current = [];
            document.title = originalTitleRef.current;
        };
    }, [
        store.view,
        productName
    ]);
}
export { useHtmlTitle };
